"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Heart,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  MessageSquare,
  Shield,
  ArrowRight,
  Crown,
  Mic,
  Layers,
  CheckCircle2,
  Award,
  BookOpen,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { CardGridBlock } from "@/lib/blocks/card-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const EVIDENCE = [
  { metric: "83.3", suffix: "%", label: "Campaign Success", sub: "15 victories / 18 engagements", accent: "emerald" },
  { metric: "70", suffix: "%+", label: "Women in Teams", sub: "By selection, results", accent: "coral" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years clean record", accent: "red" },
  { metric: "99.9999", suffix: "%", label: "Uptime", sub: "31.5 seconds max/year", accent: "cyan" },
];

const TRAITS = [
  { num: "01", title: "Rigor", body: "The ability to follow protocols and maintain standards under pressure. Non-negotiable in sovereign-scale operations." },
  { num: "02", title: "Communication", body: "The willingness to surface problems early and clearly, before they escalate into crises." },
  { num: "03", title: "Risk Discipline", body: "The capacity to manage risk carefully rather than recklessly, to follow systems that distinguish excellence from luck." },
  { num: "04", title: "Retention", body: "The likelihood of staying long enough to build institutional knowledge. The irreplaceable asset that walks out with every departure." },
  { num: "05", title: "Collaboration", body: "The ability to make the people around them better, not just to perform individually." },
];

const ADVANTAGES = [
  { num: "01", title: "Communication", body: "Problems surface earlier because team members are willing to say the uncomfortable thing in the room before it becomes a catastrophe in the boardroom.", accent: "red" },
  { num: "02", title: "Risk Discipline", body: "Protocols and systems that distinguish operational excellence from operational luck are followed meticulously. The 99.9999% uptime standard is the product of this discipline.", accent: "purple" },
  { num: "03", title: "Collaboration", body: "Internal energy goes outward, toward the mission, instead of inward, toward each other. Freeing up to forty percent of productive capacity typically lost to internal politics.", accent: "emerald" },
  { num: "04", title: "Retention", body: "Institutional knowledge preserved because the environment is one where people can stay and thrive. Every departure is a loss. High retention is operational.", accent: "cyan" },
  { num: "05", title: "Ego Management", body: "Ego interferes less with execution. The operational systems function as designed. In sovereign-scale operations, ego management is an architectural requirement.", accent: "amber-gold" },
];

const IMPLEMENTATION = [
  { num: "01", title: "Define the Traits", body: "Rigor. Communication. Risk discipline. Retention. Collaboration. Each must be defined in operational terms — observable behaviors that can be assessed and verified.", accent: "red" },
  { num: "02", title: "Redesign Selection", body: "Blind to gender and optimized for the five traits. Structured interviews with standardized scoring rubrics. Work simulations that test actual operational behavior.", accent: "purple" },
  { num: "03", title: "Design the Environment", body: "Communication protocols. Risk management frameworks. Collaborative structures. Retention mechanisms. Outcome accountability. The environment must reinforce the five traits.", accent: "cyan" },
  { num: "04", title: "Measure Outcomes", body: "The operating model is validated by outcomes — team performance, retention, risk incidents, problem identification speed — not by the composition of the team.", accent: "emerald" },
  { num: "05", title: "Iterate", body: "Every team built, every outcome measured, every lesson learned feeds back into the selection process. The clinical method applied to team building. The model compounds.", accent: "amber-gold" },
];

export default function WomenInLeadershipPage() {
  return (
    <>
      <PageHero
        icon={Heart}
        overline="Women in Leadership"
        overlineClassName="text-coral"
        title={
          <>
            The Operating Case. <span className="text-gradient-premium">A Decade of Evidence.</span>
          </>
        }
        subtitle="The world debates whether women belong in leadership. Dr. Jyoti Kush has spent a decade proving they outperform — and has the operational data to demonstrate it. Teams that are, in most offices, over seventy percent women. Not by policy. Not by quota. By performance. By selection. By the measurable, verifiable, sovereign-grade outcomes these teams produce."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Women in Leadership" }]}
        badge="The talent the world overlooks"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-5`}>
              <Sparkles className="size-3.5 text-coral" />
              <span className="text-overline text-coral">The Premise</span>
            </div>
            <h2 className="text-display-md mb-5">
              The world&apos;s most reliable talent pool is the one the world is systematically overlooking.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The world has a talent allocation problem. Organizations select leaders on the wrong signals — confidence mistaken for competence, volume mistaken for capability, self-promotion mistaken for results. The most reliable operational performers — the people who communicate clearly, manage risk carefully, follow protocols rigorously, stay with the organization, and make others better — are being passed over.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The argument is from operations, not morality. From evidence, not ideology. From outcomes, not aspirations. A decade of sovereign-scale engagements across eighteen countries on three continents demonstrates the case with the authority only verified outcomes can command.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Target className="size-3.5 text-red" />
            <span className="text-overline text-red">The Five Selection Traits</span>
          </div>
          <h2 className="text-display-md mb-4">Optimize for these. The talent pool emerges.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Optimize hiring for the five traits that produce reliable operational outcomes — and the talent pool that emerges is consistently majority-women. Not by design. By selection.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {TRAITS.map((t, i) => (
            <motion.div
              key={t.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="relative glass-card rounded-2xl p-5 bg-gradient-to-br from-coral/10 to-transparent border border-coral/20"
            >
              <div className="text-display-sm font-bold text-coral/30 leading-none mb-3">{t.num}</div>
              <h3 className="text-heading-md mb-2 text-coral">{t.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed">{t.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Award className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Five Operating Advantages</span>
          </div>
          <h2 className="text-display-md mb-4">Why majority-female teams outperform.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Five observed, documented, and verified advantages — across a decade of sovereign-scale operations.
          </p>
        </div>
        <div className="space-y-4">
          {ADVANTAGES.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr_auto] gap-5 items-center">
                  <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${ac.ring}`}>
                    <span className="text-display-sm font-bold text-white">{a.num}</span>
                  </div>
                  <div>
                    <h3 className={`text-display-sm mb-2 ${ac.text}`}>{a.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <TrendingUp className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Talent Arbitrage</span>
          </div>
          <h2 className="text-display-md mb-4">The most reliable competitive advantage available.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The market is mispricing this talent. Organizations that recognize the mispricing and redesign their selection processes capture a structural advantage that compounds over time.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4 }}
            className="glass-card rounded-2xl p-6 bg-gradient-to-br from-purple/10 to-transparent border border-purple/20"
          >
            <MessageSquare className="size-8 text-purple mb-4" />
            <h3 className="text-heading-md mb-3 text-purple">The Mispricing Mechanism</h3>
            <p className="text-body-sm text-muted-foreground leading-relaxed">
              The current selection system systematically overvalues confidence, self-promotion, and dominance. It systematically undervalues communication, risk discipline, and collaboration. This creates a gap: the talent that produces results is available at a discount because the market is not bidding on it.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="glass-card rounded-2xl p-6 bg-gradient-to-br from-emerald/10 to-transparent border border-emerald/20"
          >
            <Shield className="size-8 text-emerald mb-4" />
            <h3 className="text-heading-md mb-3 text-emerald">The Compounding Advantage</h3>
            <p className="text-body-sm text-muted-foreground leading-relaxed">
              Organizations that move first — that adopt the model before it becomes standard practice — capture the advantage while it is still available. The data set compounds. The selection process sharpens. The performance gap widens with every cycle.
            </p>
          </motion.div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Users className="size-3.5 text-denim" />
            <span className="text-overline text-denim">The Implementation Framework</span>
          </div>
          <h2 className="text-display-md mb-4">Five steps to adopt the model.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The 70% Operating Model is not a one-time initiative. It is an ongoing operational practice that produces compounding returns.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {IMPLEMENTATION.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{s.num}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{s.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="leadership-pillars" ariaLabel="Leadership Pillars">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Award className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Leadership Pillars</span>
          </div>
          <h2 className="text-display-md mb-4">Seven pillars. One operating case for overlooked talent.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature framework. Each pillar represents a domain where the talent the world overlooks demonstrates consistent operational advantage — verified across a decade of sovereign-scale engagements.
          </p>
        </div>
        <PillarGridBlock
          columns={4}
          variant="numbered"
          pillars={[
            { title: "Diagnose Before Treating", body: "The clinical discipline. Identify the actual problem, not the surface presentation. The most expensive organizational failures trace to misdiagnosis.", icon: <Target className="size-5" />, accent: "red" },
            { title: "Communicate the Hard Truth", body: "The willingness to surface problems early and clearly. The cost of suppression is always higher than the cost of disclosure. Information hoarded is information weaponized.", icon: <MessageSquare className="size-5" />, accent: "purple" },
            { title: "Triage Under Pressure", body: "The ability to sequence under fear. What is fatal now. What can wait. The leader who cannot rank is the leader who will fail.", icon: <Shield className="size-5" />, accent: "cyan" },
            { title: "Follow the Protocol", body: "The discipline that prevents degradation under pressure. The protocol carries the ninety-five percent. The mature operator loves the protocol and knows when to put it down.", icon: <CheckCircle2 className="size-5" />, accent: "emerald" },
            { title: "Own the Outcome", body: "Effort is not achievement. Outcome is the measure. The leader who is measured by results, not by activity, transforms the culture around them.", icon: <TrendingUp className="size-5" />, accent: "amber-gold" },
            { title: "Make Others Better", body: "The collaboration discipline. Internal energy goes outward toward the mission, not inward toward each other. Freeing capacity that politics consumes.", icon: <Users className="size-5" />, accent: "denim" },
            { title: "Stay and Build", body: "The retention discipline. Institutional knowledge preserved. The irreplaceable asset that walks out with every departure. The talent the world systematically overlooks stays.", icon: <Heart className="size-5" />, accent: "lime" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <BookOpen className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The 70% Project</span>
          </div>
          <h2 className="text-display-md mb-4">The flagship initiative. The documentary evidence.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The 70% Project is the public-facing initiative that documents the operating case for the talent the world overlooks. Not the moral case. The operating case. Backed by a decade of sovereign-scale evidence.
          </p>
        </div>
        <CardGridBlock
          columns={3}
          items={[
            { title: "The Evidence Base", body: "A decade of operational data from sovereign-scale engagements. The talent pool that emerged when selection optimized for operational traits is overwhelmingly female. The data demonstrates the case.", icon: <Award className="size-5" />, accent: "red" },
            { title: "The Implementation Framework", body: "A reproducible methodology for adopting the 70% Operating Model. Define the traits, redesign selection, design the environment, measure outcomes, iterate.", icon: <Target className="size-5" />, accent: "purple" },
            { title: "The Speaking Engagements", body: "Keynote presentations that translate the evidence into actionable leadership practice. Past stages include the Nobel Peace Prize, the World Economic Forum, and leadership forums globally.", icon: <Mic className="size-5" />, accent: "cyan" },
            { title: "The Advisory Practice", body: "Board service, executive advisory, and consulting for organizations committed to the operating case. The model is replicable. The evidence is the proof.", icon: <Users className="size-5" />, accent: "emerald" },
            { title: "The Network of Adopters", body: "A growing community of organizations that have adopted the model. Peer learning, shared measurement frameworks, collective advocacy for the operating case.", icon: <TrendingUp className="size-5" />, accent: "amber-gold" },
            { title: "The Public Conversation", body: "The argument reframed. From diversity initiative to talent strategy. From moral case to operating case. The data has done the work — the conversation follows.", icon: <MessageSquare className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <TrendingUp className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Decadal Performance Record</span>
          </div>
          <h2 className="text-display-md mb-4">Ten years. Eighteen countries. One talent strategy.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric traces to operational data. The case is not aspirational. The case is the verified record of sovereign-scale performance by teams that emerged when selection optimized for the traits that produce outcomes.
          </p>
        </div>
        <CounterBlock
          columns={4}
          stats={[
            { value: 83.3, suffix: "%", label: "Campaign Success", description: "Fifteen victories in eighteen engagements", accent: "emerald" },
            { value: 70, suffix: "%+", label: "Women in Teams", description: "By selection, not by quota", accent: "coral" },
            { value: 0, label: "Security Incidents", description: "Across fifteen-plus years", accent: "red" },
            { value: 99.9999, suffix: "%", label: "Uptime", description: "Thirty-one point five seconds maximum per year", accent: "cyan" },
            { value: 18, label: "Countries Operated", description: "Across three continents", accent: "purple" },
            { value: 9, label: "Proprietary Platforms", description: "Built in-house over fifteen-plus years", accent: "amber-gold" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-coral/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-coral mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage on women in leadership.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Keynotes, advisory, board service, and consulting engagements. The argument from operations, not morality. The evidence is a decade of sovereign-scale outcomes.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Book Dr. Jyoti Kush
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/the-70-percent-project/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Heart className="size-4" />
                The 70% Project
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: Heart, accent: "coral" },
          { title: "The 70% Project", description: "The operating case for the talent the world overlooks. A decade of verified evidence.", href: "/the-70-percent-project/", icon: Target, accent: "emerald" },
          { title: "Women's Empowerment", description: "The operating case, the evidence, the movement. The 70% Project as flagship initiative.", href: "/expertise/womens-empowerment/", icon: Award, accent: "amber-gold" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "red" },
        ]}
      />
    </>
  );
}
